import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';

interface BarberHeaderProps {
    title: string;
    subtitle?: string;
    showBackButton?: boolean;
    backPath?: string;
    rightContent?: React.ReactNode;
}

const BarberHeader: React.FC<BarberHeaderProps> = ({
    title,
    subtitle,
    showBackButton = false,
    backPath = '/barber/dashboard',
    rightContent
}) => {
    const navigate = useNavigate();
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    const handleLogout = () => {
        authService.logout();
        navigate('/login');
    };

    return (
        <>
            <header style={{
                backgroundColor: 'var(--surface)',
                borderBottom: '1px solid var(--secondary)',
                padding: '0.75rem 0',
                marginBottom: '1.5rem',
                position: 'sticky',
                top: 0,
                zIndex: 100
            }}>
                <div className="container">
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '1rem'
                    }}>
                        {/* Left side: Back button + Title */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {showBackButton && (
                                <button
                                    onClick={() => navigate(backPath)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        padding: '0.5rem 0.75rem',
                                        backgroundColor: 'var(--secondary)',
                                        border: 'none',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        color: 'var(--text-primary)',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--secondary-light)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--secondary)';
                                    }}
                                >
                                    ← Volver
                                </button>
                            )}
                            <div>
                                <h1 style={{
                                    margin: 0,
                                    fontSize: '1.25rem',
                                    fontWeight: '700',
                                    color: 'var(--accent)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    {title}
                                </h1>
                                {subtitle && (
                                    <p style={{
                                        margin: '0.25rem 0 0 0',
                                        fontSize: '0.875rem',
                                        color: 'var(--text-secondary)'
                                    }}>
                                        {subtitle}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Right side: Custom content + Logout */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
                            {rightContent}
                            <button
                                onClick={() => setShowLogoutConfirm(true)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    padding: '0.5rem 0.75rem',
                                    backgroundColor: 'transparent',
                                    border: '1px solid var(--secondary)',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    color: 'var(--text-secondary)',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--error)';
                                    e.currentTarget.style.color = 'var(--error)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--secondary)';
                                    e.currentTarget.style.color = 'var(--text-secondary)';
                                }}
                            >
                                🚪 Salir
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'var(--surface)',
                        borderRadius: '1rem',
                        padding: '1.5rem',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                        border: '1px solid var(--secondary)'
                    }}>
                        <h3 style={{
                            margin: '0 0 0.5rem 0',
                            fontSize: '1.25rem',
                            color: 'var(--text-primary)'
                        }}>
                            🚪 ¿Desea cerrar sesión?
                        </h3>
                        <p style={{
                            color: 'var(--text-secondary)',
                            marginBottom: '1.5rem',
                            fontSize: '0.9rem'
                        }}>
                            Se cerrará tu sesión actual y volverás a la página de login.
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '0.75rem',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={() => setShowLogoutConfirm(false)}
                                style={{
                                    padding: '0.625rem 1.25rem',
                                    backgroundColor: 'var(--secondary)',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    color: 'var(--text-primary)'
                                }}
                            >
                                No, cancelar
                            </button>
                            <button
                                onClick={handleLogout}
                                style={{
                                    padding: '0.625rem 1.25rem',
                                    backgroundColor: 'var(--error)',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    color: 'white'
                                }}
                            >
                                Sí, cerrar sesión
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BarberHeader;
